// import composables
import { useAuthStore } from '@/store/auth/authStore';
import useToken from '@/composables/useToken/useToken';
import router from '@/router';
import identityService from '@/services/identityService';

// import types
import ApiLoginResponse from '@/types/ApiLogInResponse';
import ApiRefreshTokenResponse from '@/composables/useAuth/types/ApiRefreshTokenResponse';
import Routes from '@/enums/Routes';

const useAuth = () => {
  const {
    setUser,
    removeUser,
    setAuth,
    setToken,
    removeAuth,
    getUser,
    getToken,
    getRefreshToken,
    getRefreshTokenTimeout,
  } = useAuthStore();

  const { decodeToken, isExpired } = useToken();

  function logInUser(email: string, password: string, otp: string = '') {
    const fetch = identityService.logInUser(email, password, otp);
    fetch.onFetchResponse((response: any) => {
      response.json().then((res: ApiLoginResponse) => {
        if (
          res.status == 'error' ||
          res.data === 'OTP mismatch' ||
          res.data === 'User not found' ||
          res.data === 'failed'
        ) {
          throw Error('API status:' + res.status + ' ' + res.data);
        }

        const jwtToken = decodeToken(res.data.token);

        setUser({
          id: jwtToken.nameIdentifier,
          username: jwtToken.name,
          email: email,
          role: jwtToken.role,
        });

        setAuth({
          token: res.data.token,
          refreshToken: res.data.refreshToken,
        });

        router.push(Routes.HOME);
      });
    });

    fetch.onFetchError((error: any) => {
      console.error(error);
    });
    return fetch;
  }

  function logOutUser() {
    const refreshToken = getRefreshToken; // Retrieve refresh token
    const token = getToken; // Retrieve regular token

    // Array to hold fetch promises
    const fetchPromises = [];
    if (refreshToken) {
      const promise1 = new Promise((resolve, reject) => {
        const fetch = identityService.logOutUser(refreshToken || '');

        fetch.onFetchResponse((response: any) => {
          return resolve(response);
        });

        fetch.onFetchError(() => {
          return reject(
            Error('Error during logout: refresh token not revoked successfully')
          );
        });
      });
      fetchPromises.push(promise1);
    }
    if (token) {
      const promise2 = new Promise((resolve, reject) => {
        const fetch = identityService.logOutUser(token || '');

        fetch.onFetchResponse((response: any) => {
          return resolve(response);
        });

        fetch.onFetchError(() => {
          return reject(
            Error('Error during logout: token not revoked successfully')
          );
        });
      });
      fetchPromises.push(promise2);
    }

    // Define the success handler
    const onSuccess = () => {
      removeUser();
      removeAuth();
      router.push(Routes.LOGIN); // Redirect to the login page
    };

    // Define the error handler
    const onError = (error: Error) => {
      Error('Error during logout: ' + error.message);
    };

    const allPromises = Promise.all(fetchPromises);

    allPromises.then(onSuccess);
    allPromises.catch(onError);

    return allPromises;

    //If there are fetch operations, execute them
  }

  function changeUserPassword(
    userId: string,
    oldPassword: string,
    newPassword: string
  ) {
    const fetch = identityService.changeUserPassword(
      userId,
      oldPassword,
      newPassword
    );

    fetch.onFetchResponse(() => {
      //handle response
    });

    fetch.onFetchError((error: any) => {
      console.error(error);
    });
    return fetch;
  }

  function tokenRefresh() {
    // that is needed to obtain token correctly as getRefreshToken is not updated from some reason

    if (getRefreshToken === '' || getRefreshToken === null) {
      logOutUser();
      return;
    }

    const fetch = identityService.tokenRefresh(getRefreshToken);

    fetch.onFetchResponse((response: any) => {
      response.json().then((x: ApiRefreshTokenResponse) => {
        if (
          x.status == 'error' ||
          x.data == 'User not found' ||
          x.data == 'OTP mismatch' ||
          x.data == 'failed'
        ) {
          throw Error('API status:' + x.status);
        }
        setToken(x.data.jwtToken);
      });
    });

    fetch.onFetchError((error: Error) => {
      console.error(error.message);
      logOutUser();
    });

    return fetch;
  }

  function isAuthorized() {
    if (!!getToken && !!getRefreshToken) {
      return true;
    }
    return false;
  }

  function isTokenExpired() {
    if (!getToken) {
      return true;
    }
    return isExpired(getToken);
  }

  function isRefreshTokenExpired() {
    if (!getRefreshToken) {
      return true;
    }
    return isExpired(getRefreshToken);
  }

  return {
    logInUser,
    logOutUser,
    changeUserPassword,
    isAuthorized,
    isTokenExpired,
    isRefreshTokenExpired,
    tokenRefresh,
    getToken,
    getRefreshToken,
    getRefreshTokenTimeout,
    getUser,
  };
};

export default useAuth;
